import { Asset } from '@cube3/common/model/schema/resources/asset';
import { useCallback, useEffect, useState } from 'react';
import { Folder } from '@cube3/common/model/schema/resources/folder';
import { NodeTreeType } from './useReviewNodeTree';
import { matchPath, useLocation } from 'react-router';
import { urlStructureReviewLinkAsset } from '../../../routing/routingPaths';

export const useRenderFolders = (nodeTree: NodeTreeType) => {
  /** Use to check if a folder is expanded */
  const [expandedFolders, setExpandedFolders] = useState<{
    [parentId: string]: boolean;
  }>({});
  const onClickFolder = useCallback((id: string) => {
    setExpandedFolders((prev) => {
      return { ...prev, [id]: !prev[id] };
    });
  }, []);

  const getChildren = useCallback(
    (id: string) => {
      const children = nodeTree?.[id];
      const assets = children?.filter(
        (item) => item.type === 'asset'
      ) as Asset[];
      const folders = children?.filter(
        (item) => item.type === 'folder'
      ) as Folder[];
      return { folders, assets };
    },
    [nodeTree]
  );

  const isExpanded = useCallback(
    (id: string) => {
      return expandedFolders[id];
    },
    [expandedFolders]
  );

  const { pathname } = useLocation();
  const matchedAsset = matchPath<{ token: string; assetId: string }>(
    pathname,
    urlStructureReviewLinkAsset
  );
  const matchedAssetId = matchedAsset && matchedAsset.params.assetId;

  /** find all the ancestors of a given folder */
  const getParent = useCallback(
    (folderId: string, ids: string[]) => {
      if (!nodeTree) return [];
      const found = Object.keys(nodeTree).find((id) =>
        nodeTree[id].find((i) => i.type === 'folder' && i.id === folderId)
      );
      if (found) {
        ids.push(found);
        getParent(found, ids);
      }
      return ids;
    },
    [nodeTree]
  );

  /** find all the ancestors of an asset */
  const getAncestors = useCallback(
    (assetId: string): string[] => {
      const ancestors = [];
      if (!nodeTree) return [];
      Object.keys(nodeTree).forEach((id) => {
        const found = nodeTree[id].find(
          (i) => i.type === 'asset' && i.id === assetId
        );
        if (found) {
          ancestors.push(id);
          return getParent(id, ancestors);
        }
      });
      return ancestors;
    },
    [nodeTree]
  );

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (mounted || !matchedAssetId) return;
    const ancestors = getAncestors(matchedAssetId);
    if (!ancestors?.length) return;
    const notExpanded = ancestors.filter((id) => !isExpanded(id));
    if (notExpanded.length) {
      notExpanded.forEach((i) => {
        setExpandedFolders((prev) => ({ ...prev, [i]: true }));
      });
    }
    setMounted(true);
  }, [mounted, matchedAssetId]);

  return {
    onClickFolder,
    getChildren,
    isExpanded
  };
};
